import React, { useState } from "react";
import styles from "./addFeature.module.css";
import { Input } from "../../Common";
import { createFeature, getComponentList } from "../../api";
import Button from "../../Common/Button";
import { productList } from "../../utils/utils";
import { validateForm } from "../../utils/validation";
import { Spinner } from "react-bootstrap";
import { DropDown } from "../../Common";
import withFeatureActions from "../../withFeatureActions";

function AddFeature({ featureAccess }) {
  const [featureName, setFeatureName] = useState("");
  const [componentName, setComponentName] = useState("");
  const [newComponentName, setNewComponentName] = useState("");
  const [product, setProduct] = useState("");
  const [componentNameList, setComponentNameList] = useState([]);
  const [isAddingNewComponent, setIsAddingNewComponent] = useState(false);
  const [loader, setLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [componentListLoading, setComponentListLoading] = useState(false);
  const resetForm = () => {
    setFeatureName("");
    setComponentName("");
    setNewComponentName("");
    setIsAddingNewComponent(false);
    setProduct("");
    setLoader(false);
  };

const handleCreateFeature = () => {
  setErrorMessage("");
  setSuccessMessage("");

  const componentToSend = isAddingNewComponent
    ? newComponentName
    : componentName;

  const formData = {
    featureName,
    componentName: componentToSend,
    product,
  };

  const requiredFields = isAddingNewComponent
    ? ["componentName", "product"]
    : ["featureName", "componentName", "product"];
  const isValid = validateForm(requiredFields, setErrors, formData);

  if (isValid) {
    callCreateFunction(componentToSend);
  }
};

  const callCreateFunction = async (componentToSend) => {
    setErrorMessage("");
    setSuccessMessage("");
    setLoader(true);

    let payload = { source: product, componentName: componentToSend };

    if (isAddingNewComponent) {
      payload = { ...payload, isParent: true, featureName: componentToSend };
    } else {
      const existingComponent = componentNameList.find(
        (comp) => comp.value === componentToSend
      );
      const componentId = existingComponent?.componentId;

      if (!componentId) {
        setErrorMessage("Component ID not found for selected component.");
        setLoader(false);
        return;
      }

      payload = {
        ...payload,
        isParent: false,
        componentId,
        featureName: featureName,
      };
    }

    try {
      const res = await createFeature(payload);
      if (res?.data?.code === 200) {
        setSuccessMessage(res?.data?.data || "Feature created successfully!");
        setTimeout(() => {
          setSuccessMessage("");
          resetForm();
        }, 3000);
      } else {
        setErrorMessage(res?.data?.message || "Failed to create feature.");
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "API call failed.");
    } finally {
      setLoader(false);
    }
  };

  const handleComponentList = async () => {
    setErrorMessage("");

    if (!product) {
      setErrorMessage("Please select a product first.");
      return;
    }
    setComponentListLoading(true);
    try {
      const response = await getComponentList(product);
      if (response?.status === 200 && response.data?.code === 200) {
        const components = response.data.data.map((item) => ({
          label: item.componentName,
          value: item.componentName,
          componentId: item.dashboardFeatureId,
        }));
        setComponentNameList(components);
      } else {
        setErrorMessage(
          response?.data?.message || "Failed to fetch component list."
        );
      }
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || "Error fetching component list."
      );
    }
    setComponentListLoading(false);
  };

  const handleComponentChange = (item) => {
    if (item === "Add New") {
      setIsAddingNewComponent(true);
      setComponentName("Add New");
    } else {
      setIsAddingNewComponent(false);
      setComponentName(item);
    }
  };

  return (
    <div className={styles.containerCardRoles}>
      <div className={styles.createRoleCard}>
        <div className={styles.card}>
          {featureAccess?.writeAccess ? (
            <>
              <h3>Create a New Feature</h3>
              <form>
                <DropDown
                  label="Product"
                  name="product"
                  value={product}
                  data={productList}
                  onChange={(item) => setProduct(item)}
                  placeholder="Select Product"
                  required
                  error={!!errors.product}
                  helperText={errors.product}
                />
                <DropDown
                  label="Component Name"
                  name="componentName"
                  value={componentName}
                  data={[{ label: "Add New", value: "Add New" }, ...componentNameList]}
                  onChange={handleComponentChange}
                  placeholder="Select Component"
                  required
                  error={!!errors.componentName}
                  helperText={errors.componentName}
                  onOpen={handleComponentList}
                  loading={componentListLoading}
                />

                {isAddingNewComponent ? (
                  <Input
                    label="New Component Name"
                    placeholder="Enter New Component Name"
                    onChange={(e) => setNewComponentName(e.target.value)}
                    name="newComponentName"
                    value={newComponentName}
                    error={!!errors.componentName}
                    helperText={errors.componentName}
                  />
                ) : (
                  <Input
                    label="Feature Name"
                    placeholder="Enter Feature Name"
                    onChange={(e) => setFeatureName(e.target.value)}
                    name="featureName"
                    value={featureName}
                    error={!!errors.featureName}
                    helperText={errors.featureName}
                  />
                )}
              </form>

              {errorMessage && (
                <div className={styles.errorMessage}>{errorMessage}</div>
              )}
              {successMessage && (
                <div className={styles.successMessage}>{successMessage}</div>
              )}

              <Button
                classes={styles.featureButton}
                onClick={handleCreateFeature}
                disabled={loader}
                title="Create Feature"
                loading={loader}
              />
            </>
          ) : (
            <div className={styles.errorMessage}>
              You do not have permission to create features.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
export default withFeatureActions(AddFeature, "ADMIN", "Add Features");
